import * as React from "react";
import { type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { composeRippleProps, useTouchRipple } from "@/components/touch-ripple";
import { cn } from "@/lib/utils";
import { buttonVariants } from "./button-variants";
import { CircularProgress } from "./circular-progress";

// The app's button. Every button on a ported screen is an MUI one — the donor's
// dialogs use `@modsy/react-ui`'s `PrimaryDialogButton`/`SecondaryDialogButton`,
// its toolbars use `Button` and `IconButton` — and all of those are a
// `ButtonBase`, so **pressing one ripples**. That made the ripple a property of
// the button rather than of any screen: wiring it per page (as the logout page
// once had to) is how one page ends up with press feedback and the next without.
//
// Three surfaces deliberately do not ripple:
//
//   - `asChild`, because `Slot` renders exactly one child and the ripple layer
//     would be a second one. The caller composing its own element owns its own
//     feedback — unless it asks for the ripple with `rippleAsChild`, which puts
//     the layer *inside* that one child. `LinkButton` does: MUI's `Button
//     component='a'` is still a `ButtonBase`, so its anchor ripples
//     (`Button.js:579`). Opt-in rather than the default because the other
//     `asChild` callers dress Radix primitives and router links whose feedback
//     is their own,
//   - `variant="link"`, which is MUI's `Link` rather than a `ButtonBase`. A wash
//     across a run of inline text is not what pressing a link looks like,
//   - `disableRipple`, which is the donor's own opt-out. Its caller **on these
//     screens** is the dialog action row: `DialogButton` passes `disableRipple`
//     (`react-ui/src/dialog/dialog-buttons.tsx:11`), so every footer button in
//     the app is a button that does not ripple — the rule `ripple.ts` states
//     and the wrappers had no way to honour. It is not the donor's only one:
//     the specifications domain turns it off on two more
//     (`ColumnFilterPopover.tsx:621,654`), on a screen Atlas has not ported
//     yet — so a second caller here is expected, not a smell.
//
// A third opts out by hand, through `disableRipple` — MUI's own prop name,
// because it is MUI's own opt-out: the donor's `DialogButton` passes it
// (`packages/react-ui/src/dialog/dialog-buttons.tsx:11`), so every dialog action
// button in the source stays dry under the finger. `AppDialog`'s
// `DialogPrimaryButton`/`DialogSecondaryButton` are the callers **on these
// screens**, and they are not the donor's only ones: the specifications domain
// turns it off on two more (`ColumnFilterPopover.tsx:621,654`), on a screen
// Atlas has not ported yet — so a second caller here is expected, not a smell.
//
// **`loading` is MUI's loading button**, which the donor's shared `Button`
// hands to every one of its callers (`react-ui/src/button/button.tsx:22-25`:
// `loading`, a `CircularProgress size={20} color='inherit'` indicator, and
// `loadingPosition='start'`). It is not "a spinner before the label" — MUI pins
// the indicator `position: absolute; left: 14` (`Button.js:381-405`) and pushes
// the label right with an invisible 1em placeholder in a synthesised start-icon
// slot, `marginLeft: -4, marginRight: 8, opacity: 0` (`Button.js:307-311`,
// `:320-328`, `:537-543`). So the spinner sits hard against the button's left
// edge and the label does not move as it appears. Drawing it inline instead —
// which four of this app's dialogs did — puts it ~42px to the right of the
// donor's and slides the label sideways.
//
// A loading button is disabled, as MUI's is. `asChild` ignores `loading`: the
// caller composing its own element owns its own in-flight state, and `Slot`
// renders exactly one child.

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  disableRipple = false,
  rippleAsChild = false,
  disabled,
  children,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  onContextMenu,
  onDragLeave,
  onTouchMove,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    /** MUI's opt-out, for the one surface the donor turns the ripple off on. */
    disableRipple?: boolean;
    /** Ripple under `asChild` too, the layer inside the one child — see above. */
    rippleAsChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";
  const busy = loading && !asChild;
  const { rippleTargetProps, rippleLayer } = useTouchRipple({
    disabled: Boolean(disabled) || busy,
  });
  const ripples = (!asChild || rippleAsChild) && variant !== "link" && !disableRipple;

  // The caller's handlers are kept, not replaced: `onPointerDown` is a plausible
  // thing to pass a button, and silently dropping it would be a bug that only
  // shows up on the one screen that passes one.
  //
  // **All eleven arms, `focusRipple` on.** `Button.js:579` passes
  // `focusRipple: !disableFocusRipple`, so in the source a tabbed-to button
  // inks under Space just as it does under the finger.
  //
  // This is also an owner ruling being honoured late rather than a new
  // behaviour: #456's D5 was ruled **(b) "fix the hook now"**, and its scope
  // note named this file first — "every surface on `useTouchRipple` gains
  // this: `button.tsx`, …". The hook gained the arms; this button went on
  // picking four of them by hand, so the ruling never actually reached it.
  const arms = composeRippleProps<HTMLButtonElement>(
    rippleTargetProps,
    {
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerCancel,
      onKeyDown,
      onKeyUp,
      onFocus,
      onBlur,
      onContextMenu,
      onDragLeave,
      onTouchMove,
    },
    { disableRipple: !ripples, focusRipple: true },
  );

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      // `relative` so the ripple layer, which is `absolute inset-0`, fills the
      // button rather than the nearest positioned ancestor.
      className={cn(
        buttonVariants({ variant, size, className }),
        // `relative` so the ripple layer, which is `absolute inset-0`, and the
        // loading indicator, which is `absolute left-[14px]`, both resolve
        // against the button rather than the nearest positioned ancestor.
        (ripples || busy) && "relative",
        // **MUI's Button root sets no `gap`.** The whole displacement of a
        // loading label is the start-icon slot: `-4 + 22 + 8 = 26px`
        // (`Button.js:306-307`, `:477-481`). This app's base adds `gap-2` on
        // top, which stood every loading label 8px right of the donor's — 58px
        // in against modsy's 49px on the Request Item dialog. Zeroing the gap
        // while busy leaves the spacer's own `mr-2` as the entire separation,
        // which is exactly MUI's 26, and it does it without disturbing the
        // resting button.
        busy && "gap-0",
      )}
      disabled={disabled || busy}
      // Caller props first, arms last. The eleven arm names are destructured
      // out of `props` above, so neither order can clobber today — but with
      // the arms last it stays that way if a twelfth arm is ever added, where
      // the other order would lose it to `props` silently and with no type
      // error. It is also the order `RippleButton` and `Chip` already use.
      {...props}
      {...arms}
    >
      {/* One child when `asChild`, or `Slot`'s `Children.only` throws — a
          `{false}` sibling counts. */}
      {asChild ? (
        ripples ? (
          withinChild(children, rippleLayer)
        ) : (
          children
        )
      ) : ripples || busy ? (
        <>
          {busy ? (
            <>
              {/* **Both numbers below are MUI's `size='large'` ones**, and the
                  fix is not to derive them from this component's `size` prop.
                  MUI's placeholder is `1em` at the size's icon font-size —
                  18/20/22 for small/medium/large (`Button.js:54-81`, `:477-481`)
                  — and the indicator sits at `left: 14`, but `10` at
                  `size='small'` and `6` for `variant='text'`
                  (`Button.js:404-424`).

                  **Every call site that passes `loading` is MUI-large**, which
                  is what makes one pair of numbers right rather than lucky: the
                  donor puts every dialog footer button through `DialogButton`,
                  and that hardcodes `size='large'`
                  (`react-ui/src/dialog/dialog-buttons.tsx:11-27`). Both Atlas
                  call sites reach this component through `MuiButton` at
                  `size="large"` — `DialogPrimaryButton` (`app-dialog.tsx`) and
                  the edit page's Save (`item-edit-page.tsx`) — and `MuiButton`
                  passes `size={null}` here, carrying MUI's size in its own
                  classes. So this component's `size` says nothing about MUI's
                  at either of them, and a `size`-driven table would have
                  nothing to read. Making this vary means first handing MUI's
                  size down, which is more than a spinner port.

                  The invariant is held by the call sites, so it is asserted
                  where one could drift: `catalog-batch-dialogs.layout.browser
                  .test.tsx`, "the batch dialogs' footer buttons". */}
              <span
                aria-hidden="true"
                data-slot="button-loading-spacer"
                className="mr-2 -ml-1 inline-block size-[22px] opacity-0"
              />
              {/* No `aria-hidden`. The donor's `loadingIndicator` is a bare
                  `CircularProgress` (`Button.js:479`,
                  `react-ui/src/button/button.tsx:22-24`), so its
                  `role='progressbar'` is announced — and the spacer beside it
                  is the hidden half of this pair. */}
              <CircularProgress size={20} color="inherit" className="absolute left-[14px]" />
            </>
          ) : null}
          {children}
          {ripples ? rippleLayer : null}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

/**
 * The one child `Slot` renders, with `extra` appended to its own children — how
 * a layer reaches the inside of a caller's element under `asChild`.
 */
function withinChild(child: React.ReactNode, extra: React.ReactNode) {
  if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return child;
  return React.cloneElement(child, undefined, child.props.children, extra);
}

export { Button };
